/**
 * Add-ons block.
 *
 * <addon-select>   custom, accessible listbox dropdown for choosing a variant
 * <addon-product>  one add-on card: reacts to variant changes, adds to cart (button mode)
 *                  or tracks selection (checkbox mode)
 * <addon-products> container: slider (Swiper) setup, dropdown overflow handling and,
 *                  in checkbox mode on the product page, adding ticked add-ons together
 *                  with the main product form
 */

/**
 * POSTs a multipart form body to the cart add endpoint and resolves with the parsed JSON.
 *
 * This deliberately uses XMLHttpRequest instead of fetch. Store apps such as delivery date
 * pickers monkey-patch window.fetch once the main Add to cart button has been clicked: they
 * inject their line item properties into every request that has a body, reject JSON bodies
 * (empty 400 response) and send the request twice, which doubles the quantity. They do not
 * patch XMLHttpRequest, so add-ons go to the cart exactly once with the quantity asked for.
 * Multiple items use Rails-style nested keys (items[0][id], items[0][properties][Name], ...).
 */
function addonCartAdd(formData) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${routes.cart_add_url}.js`);
    request.setRequestHeader('Accept', 'application/json');
    request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    request.onload = () => {
      try {
        resolve(JSON.parse(request.responseText));
      } catch (error) {
        reject(new Error(`Cart add failed with status ${request.status}`));
      }
    };
    request.onerror = () => reject(new Error('Cart add request failed'));
    request.send(formData);
  });
}

if (!customElements.get('addon-select')) {
  customElements.define(
    'addon-select',
    class AddonSelect extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.button = this.querySelector('.addon-select__button');
        this.list = this.querySelector('.addon-select__list');
        this.input = this.querySelector('input[name="id"]');
        this.valueLabel = this.querySelector('.addon-select__value');
        this.options = Array.from(this.querySelectorAll('[role="option"]'));
        this.activeIndex = -1;
        this.onDocumentClick = this.onDocumentClick.bind(this);

        if (!this.button || !this.list) return;

        this.button.addEventListener('click', () => this.toggle());
        this.button.addEventListener('keydown', this.onButtonKeyDown.bind(this));
        this.list.addEventListener('keydown', this.onListKeyDown.bind(this));
        this.options.forEach((option, index) => {
          option.addEventListener('click', (event) => {
            event.preventDefault();
            this.select(index);
          });
          option.addEventListener('mousemove', () => {
            if (this.activeIndex !== index) this.setActive(index, false);
          });
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.close(false);
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick, true);
      }

      get isOpen() {
        return this.button.getAttribute('aria-expanded') === 'true';
      }

      get selectedIndex() {
        return this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      }

      toggle() {
        this.isOpen ? this.close(true) : this.open();
      }

      open() {
        if (this.isOpen) return;
        this.list.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
        this.position();
        this.setActive(Math.max(this.selectedIndex, 0), true);
        document.addEventListener('click', this.onDocumentClick, true);
        this.dispatchEvent(new CustomEvent('addon:select-open', { bubbles: true }));
        this.list.focus({ preventScroll: true });
      }

      close(focusButton) {
        if (!this.isOpen) return;
        this.list.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
        this.list.removeAttribute('aria-activedescendant');
        this.classList.remove('addon-select--up');
        document.removeEventListener('click', this.onDocumentClick, true);
        if (focusButton) this.button.focus();
        this.dispatchEvent(new CustomEvent('addon:select-close', { bubbles: true }));
      }

      position() {
        const rect = this.button.getBoundingClientRect();
        const listHeight = this.list.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < listHeight + 16 && rect.top > listHeight + 16;
        this.classList.toggle('addon-select--up', openUp);
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.close(false);
      }

      onButtonKeyDown(event) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        this.open();
        this.list.focus({ preventScroll: true });
      }

      onListKeyDown(event) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.move(1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.move(-1);
            break;
          case 'Home':
            event.preventDefault();
            this.setActive(0, true);
            break;
          case 'End':
            event.preventDefault();
            this.setActive(this.options.length - 1, true);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.select(this.activeIndex);
            break;
          case 'Escape':
            event.preventDefault();
            this.close(true);
            break;
          case 'Tab':
            this.close(false);
            break;
        }
      }

      move(delta) {
        const count = this.options.length;
        if (!count) return;
        const next = (this.activeIndex + delta + count) % count;
        this.setActive(next, true);
      }

      setActive(index, scroll) {
        this.options.forEach((option, i) => option.classList.toggle('addon-select__option--active', i === index));
        this.activeIndex = index;
        const option = this.options[index];
        if (!option) return;
        this.list.setAttribute('aria-activedescendant', option.id);
        if (scroll) option.scrollIntoView({ block: 'nearest' });
      }

      select(index) {
        const option = this.options[index];
        if (!option || option.getAttribute('aria-disabled') === 'true') return;

        this.options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
        if (this.valueLabel) this.valueLabel.textContent = option.dataset.title;
        if (this.input) this.input.value = option.dataset.variantId;

        this.dispatchEvent(
          new CustomEvent('addon:variant-change', {
            bubbles: true,
            detail: {
              variantId: option.dataset.variantId,
              available: option.dataset.available === 'true',
              title: option.dataset.title,
              price: option.dataset.price,
              compareAtPrice: option.dataset.compareAtPrice,
              image: option.dataset.image,
            },
          })
        );

        this.close(true);
      }
    }
  );
}

if (!customElements.get('addon-product')) {
  customElements.define(
    'addon-product',
    class AddonProduct extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('form');
        this.button = this.querySelector('.addon-product__button');
        this.buttonText = this.querySelector('.addon-product__button-text');
        this.buttonAdded = this.querySelector('.addon-product__button-added');
        this.checkbox = this.querySelector('[data-addon-checkbox]');
        this.checkControl = this.querySelector('.addon-product__check');
        this.checkLabelOff = this.querySelector('.addon-product__check-label--off');
        this.spinner = this.querySelector('.loading__spinner');
        this.priceWrap = this.querySelector('.addon-product__price');
        this.priceCurrent = this.querySelector('.addon-product__price-current');
        this.priceCompare = this.querySelector('.addon-product__price-compare');
        this.image = this.querySelector('img.addon-product__image');
        this.error = this.querySelector('.addon-product__error');
        this.live = this.querySelector('.addon-product__live');
        this.loading = false;

        this.addEventListener('addon:variant-change', this.onVariantChange.bind(this));
        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
        if (this.checkbox) {
          this.checkbox.addEventListener('change', this.onCheckboxChange.bind(this));
          // Checkbox mode: a click anywhere on the card (outside links and controls) toggles the checkbox.
          if (this.form) this.form.addEventListener('click', this.onCardClick.bind(this));
        }
      }

      onCardClick(event) {
        if (event.target.closest('a, button, input, label, addon-select, [role="listbox"]')) return;
        if (this.checkbox.disabled || this.loading) return;
        this.checkbox.click();
      }

      get onCartPage() {
        return Boolean(document.getElementById('main-cart-items'));
      }

      get cart() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      get variantId() {
        return Number(this.dataset.variantId);
      }

      get available() {
        return !this.classList.contains('addon-product--sold-out');
      }

      /* Checkbox mode on the product page: is this add-on ticked? */
      get selected() {
        return Boolean(this.checkbox && this.checkbox.checked && !this.checkbox.disabled);
      }

      onVariantChange(event) {
        const detail = event.detail;
        this.dataset.variantId = detail.variantId;

        if (this.priceCurrent) this.priceCurrent.textContent = detail.price;
        if (this.priceCompare) {
          const onSale = Boolean(detail.compareAtPrice);
          this.priceCompare.textContent = detail.compareAtPrice || '';
          this.priceCompare.hidden = !onSale;
          if (this.priceWrap) this.priceWrap.classList.toggle('addon-product__price--on-sale', onSale);
        }

        if (detail.image && this.image) {
          this.image.removeAttribute('srcset');
          this.image.removeAttribute('sizes');
          this.image.src = detail.image;
        }

        const label = detail.available ? this.dataset.labelAdd : this.dataset.labelSoldOut;

        if (this.button) {
          this.button.disabled = !detail.available;
          if (this.buttonText) this.buttonText.textContent = label;
        }

        if (this.checkbox) {
          this.checkbox.value = detail.variantId;
          this.checkbox.disabled = !detail.available;
          this.checkbox.setAttribute('aria-label', `${label} - ${this.dataset.productTitle || ''}`.trim());
          if (this.checkControl) this.checkControl.classList.toggle('addon-product__check--disabled', !detail.available);
          if (!detail.available && this.checkbox.checked) this.setSelected(false);
        }

        this.classList.toggle('addon-product--sold-out', !detail.available);
        this.resetAdded();
        this.showError('');
      }

      onCheckboxChange() {
        if (this.onCartPage) {
          // On the cart page a ticked checkbox adds the add-on right away.
          if (this.checkbox.checked) this.addToCart();
          return;
        }
        this.setSelected(this.checkbox.checked);
      }

      setSelected(isSelected) {
        if (!this.checkbox) return;
        this.checkbox.checked = isSelected;
        this.classList.toggle('is-selected', isSelected);
        this.dispatchEvent(new CustomEvent('addon:selection-change', { bubbles: true, detail: { selected: isSelected } }));
      }

      getSectionsToRender(cart) {
        if (this.onCartPage) {
          const items = document.getElementById('main-cart-items');
          const footer = document.getElementById('main-cart-footer');
          return [items && items.dataset.id, footer && footer.dataset.id, 'cart-icon-bubble', 'cart-live-region-text'].filter(
            Boolean
          );
        }
        if (cart) return cart.getSectionsToRender().map((section) => section.id);
        return [];
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.button) return;
        this.addToCart();
      }

      addToCart() {
        if (this.loading || !this.available) return;

        const variantId = this.variantId;
        if (!variantId) return;

        this.setLoading(true);
        this.showError('');
        this.resetAdded();

        const cart = this.onCartPage ? null : this.cart;
        if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        const sections = this.getSectionsToRender(cart);
        if (sections.length) formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);

        addonCartAdd(formData)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'addon-products',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.showError(response.description || this.dataset.labelError);
              if (this.checkbox) this.checkbox.checked = false;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'addon-products',
              productVariantId: variantId,
              cartData: response,
            });

            if (this.onCartPage) {
              this.renderCartPage(response);
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            cart.renderContents(response);
            cart.classList.remove('is-empty');
            this.setAdded();
          })
          .catch((error) => {
            console.error(error);
            this.showError(this.dataset.labelError);
            if (this.checkbox) this.checkbox.checked = false;
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      renderCartPage(response) {
        const sections = response.sections || {};
        const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

        const cartItems = document.querySelector('cart-items');
        const itemsEl = document.getElementById('main-cart-items');
        const footerEl = document.getElementById('main-cart-footer');
        if (cartItems) cartItems.classList.remove('is-empty');
        if (footerEl) footerEl.classList.remove('is-empty');

        const swap = (container, sectionId, selector) => {
          const html = sectionId && sections[sectionId];
          if (!container || !html) return null;
          const doc = parse(html);
          const source = doc.querySelector(selector);
          const target = container.querySelector(selector) || container;
          if (source) target.innerHTML = source.innerHTML;
          return doc;
        };

        swap(itemsEl, itemsEl && itemsEl.dataset.id, '.js-contents');
        const footerDoc = swap(footerEl, footerEl && footerEl.dataset.id, '.js-contents');
        swap(document.getElementById('cart-icon-bubble'), 'cart-icon-bubble', '.shopify-section');

        const liveRegion = document.getElementById('cart-live-region-text');
        if (liveRegion) {
          swap(liveRegion, 'cart-live-region-text', '.shopify-section');
          liveRegion.setAttribute('aria-hidden', false);
          setTimeout(() => liveRegion.setAttribute('aria-hidden', true), 1000);
        }

        // Replace this block with the freshly rendered one so the added product disappears.
        const root = this.closest('addon-products');
        if (!root || !footerDoc) return;
        const fresh = footerDoc.getElementById(root.id);
        if (fresh) {
          root.replaceWith(fresh);
          const focusTarget =
            fresh.querySelector('.addon-product__button:not([disabled]), .addon-product__checkbox:not([disabled])') ||
            fresh.querySelector('.addon-products__heading');
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        } else {
          root.remove();
        }
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        const control = this.button || this.checkControl;
        if (control) {
          control.classList.toggle('loading', isLoading);
          control.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        }
        if (this.button) this.button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        if (this.checkbox && this.available) this.checkbox.disabled = isLoading;
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      }

      setAdded() {
        if (!this.button) return;
        this.button.classList.add('is-added');
        if (this.buttonText) this.buttonText.classList.add('hidden');
        if (this.buttonAdded) this.buttonAdded.classList.remove('hidden');
        if (this.live) this.live.textContent = this.dataset.announceAdded || '';
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => this.resetAdded(), 2200);
      }

      resetAdded() {
        clearTimeout(this.addedTimer);
        if (!this.button) return;
        this.button.classList.remove('is-added');
        if (this.buttonText) this.buttonText.classList.remove('hidden');
        if (this.buttonAdded) this.buttonAdded.classList.add('hidden');
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message || '';
        this.error.hidden = !message;
      }
    }
  );
}

if (!customElements.get('addon-products')) {
  customElements.define(
    'addon-products',
    class AddonProducts extends HTMLElement {
      constructor() {
        super();
        this.onMainFormSubmit = this.onMainFormSubmit.bind(this);
      }

      connectedCallback() {
        if (!this.initialized) {
          this.initialized = true;
          this.addEventListener('addon:select-open', (event) => {
            this.classList.add('addon-products--overflow-visible');
            const item = event.target.closest('.addon-products__item');
            if (item) item.classList.add('is-open');
          });
          this.addEventListener('addon:select-close', (event) => {
            this.classList.remove('addon-products--overflow-visible');
            const item = event.target.closest('.addon-products__item');
            if (item) item.classList.remove('is-open');
          });
          this.addEventListener('addon:selection-change', () => this.updateSummary());
        }

        if (this.interceptsMainForm) {
          // Capture on document so this runs before Dawn's product-form submit handler.
          document.addEventListener('submit', this.onMainFormSubmit, true);
          this.updateSummary();
        }

        if (this.dataset.layout === 'slider') {
          this.ensureSwiper()
            .then(() => this.initSlider())
            .catch((error) => console.error('Add-ons slider could not be initialised', error));
        }
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onMainFormSubmit, true);
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      get interceptsMainForm() {
        return (
          this.dataset.context === 'product' && this.dataset.addMode === 'checkbox' && Boolean(this.dataset.productFormId)
        );
      }

      get selectedAddons() {
        return Array.from(this.querySelectorAll('addon-product')).filter((addon) => addon.selected);
      }

      updateSummary() {
        const summary = this.querySelector('.addon-products__summary');
        if (!summary) return;
        const count = this.selectedAddons.length;
        summary.hidden = count === 0;
        if (count === 0) return;
        const template = count === 1 ? summary.dataset.summaryOne : summary.dataset.summaryOther;
        summary.textContent = (template || '').replace('[count]', count);
      }

      clearSelection() {
        this.querySelectorAll('addon-product').forEach((addon) => {
          if (addon.selected) addon.setSelected(false);
        });
        this.updateSummary();
      }

      /* Checkbox mode: add the ticked add-ons together with the main product. */
      onMainFormSubmit(event) {
        const form = event.target;
        // form.id would return the <input name="id"> element (named-property shadowing), so read the attribute.
        if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.dataset.productFormId) return;
        if (!this.isConnected) return;

        const selected = this.selectedAddons;
        if (!selected.length) return;

        event.preventDefault();
        event.stopPropagation();
        this.submitWithAddons(form, selected);
      }

      submitWithAddons(form, selected) {
        const productForm = form.closest('product-form');
        const submitButton = (productForm || form).querySelector('[type="submit"]');
        if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

        const spinner = productForm ? productForm.querySelector('.loading__spinner') : null;
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (productForm && typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage();
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', true);
          submitButton.classList.add('loading');
        }
        if (spinner) spinner.classList.remove('hidden');

        // Re-key the main form's fields as items[0][...] and append the add-ons as items[1..n].
        const formData = new FormData(form);
        const mainItem = { id: Number(formData.get('id')) };
        const body = new FormData();
        body.append('items[0][id]', formData.get('id'));
        body.append('items[0][quantity]', Number(formData.get('quantity')) || 1);
        formData.forEach((value, key) => {
          if (key === 'selling_plan' && value) body.append('items[0][selling_plan]', value);
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) body.append(`items[0][properties][${match[1]}]`, value);
        });
        selected.forEach((addon, index) => {
          body.append(`items[${index + 1}][id]`, addon.variantId);
          body.append(`items[${index + 1}][quantity]`, 1);
        });
        body.append('sections_url', window.location.pathname);
        if (cart) {
          body.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        addonCartAdd(body)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: mainItem.id,
                errors: response.errors || response.description,
                message: response.message,
              });
              if (productForm && typeof productForm.handleErrorMessage === 'function') {
                productForm.handleErrorMessage(response.description);
              }
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: mainItem.id,
              cartData: response,
            });

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // The multi-item response has no top-level key; the cart notification needs one.
            const addedItems = response.items || [];
            const mainLine = addedItems.find((item) => item.variant_id === mainItem.id) || addedItems[0];
            if (mainLine) response.key = mainLine.key;

            cart.renderContents(response);
            cart.classList.remove('is-empty');
            this.clearSelection();
          })
          .catch((error) => console.error(error))
          .finally(() => {
            if (submitButton) {
              submitButton.classList.remove('loading');
              submitButton.removeAttribute('aria-disabled');
            }
            if (spinner) spinner.classList.add('hidden');
          });
      }

      ensureSwiper() {
        if (window.Swiper) return Promise.resolve();
        if (!AddonProducts.swiperPromise) {
          AddonProducts.swiperPromise = new Promise((resolve, reject) => {
            const src = this.dataset.swiperSrc;
            let script = src && document.querySelector(`script[src="${src}"]`);
            if (!script) {
              script = document.createElement('script');
              script.src = src;
              script.defer = true;
              document.head.appendChild(script);
            }
            if (window.Swiper) return resolve();
            script.addEventListener('load', () => resolve());
            script.addEventListener('error', (error) => {
              AddonProducts.swiperPromise = null;
              reject(error);
            });
          });
        }
        return AddonProducts.swiperPromise;
      }

      initSlider() {
        const container = this.querySelector('.swiper');
        if (!container || this.swiper || !window.Swiper || !this.isConnected) return;

        this.swiper = new Swiper(container, {
          slidesPerView: 'auto',
          spaceBetween: 12,
          grabCursor: true,
          watchOverflow: true,
          roundLengths: true,
          navigation: {
            prevEl: this.querySelector('.addon-products__nav--prev'),
            nextEl: this.querySelector('.addon-products__nav--next'),
          },
          pagination: {
            el: this.querySelector('.addon-products__pagination'),
            clickable: true,
          },
          a11y: {
            prevSlideMessage: this.dataset.prevLabel,
            nextSlideMessage: this.dataset.nextLabel,
          },
          breakpoints: {
            750: { spaceBetween: 16 },
          },
          on: {
            lock: () => this.classList.add('addon-products--locked'),
            unlock: () => this.classList.remove('addon-products--locked'),
          },
        });
      }
    }
  );
}
